import { mergeClasses, Text } from "@fluentui/react-components";
import { Bot20Regular, Person20Regular } from "@fluentui/react-icons";
import DOMPurify from "dompurify";
import { useStyles } from "./answersHistory.styles";
import { IAnswersHistoryProps } from "./answersHistory.types";

export const AnswersHistory = (props: IAnswersHistoryProps): JSX.Element => {
  const styles = useStyles();
  const {
    previousAnswers,
    currentQuestion,
    objectives = [],
    isLoading,
    pendingAnswer,
    accentPalette,
  } = props;

  const objectiveNameByOrder = new Map<number, string>(
    objectives
      .filter((o) => o.name)
      .map((o, idx) => [o.order ?? idx + 1, o.name as string]),
  );

  if (previousAnswers.length === 0 && !currentQuestion) {
    return (
      <div className={styles.historyList}>
        <div className={styles.noMessagesContainer}>
          <Text>No messages</Text>
        </div>
      </div>
    );
  }

  const seenObjectiveOrders = new Set<number | undefined>();

  return (
    <div
      className={styles.historyList}
      role="log"
      aria-live="polite"
      aria-label="Interview conversation history"
    >
      {previousAnswers.map((item, idx) => {
        const objOrder = item.objective?.order;
        const isNewObjective = !seenObjectiveOrders.has(objOrder);
        if (isNewObjective) seenObjectiveOrders.add(objOrder);

        const objectiveName =
          (objOrder != null ? objectiveNameByOrder.get(objOrder) : undefined) ??
          item.objective?.name;

        return (
          <div key={idx} className={styles.messageBlock}>
            {isNewObjective && objectiveName && (
              <div className={styles.sectionDivider} aria-hidden="true">
                <span
                  className={styles.sectionDividerChip}
                  style={
                    accentPalette
                      ? {
                          backgroundColor: accentPalette.headerBg,
                          color: accentPalette.headerText,
                        }
                      : undefined
                  }
                >
                  {objectiveName}
                </span>
                <span className={styles.sectionDividerLine} />
              </div>
            )}

            <div className={styles.aiBubbleRow}>
              <div
                className={styles.aiAvatar}
                style={
                  accentPalette
                    ? {
                        backgroundColor: accentPalette.headerBg,
                        color: accentPalette.headerText,
                      }
                    : undefined
                }
                aria-hidden="true"
              >
                <Bot20Regular className={styles.icon20} />
              </div>
              <div className={styles.aiBubble}>
                {item.objectiveText}
                {item.endingMessageHTML && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(item.endingMessageHTML),
                    }}
                  />
                )}
              </div>
            </div>

            {item.answer ? (
              <div className={styles.userBubbleRow}>
                <div
                  className={styles.userBubble}
                  style={
                    accentPalette
                      ? {
                          backgroundColor: accentPalette.sidebarBg,
                          border: `1px solid ${accentPalette.sidebarBorder}`,
                          color: "#000000",
                        }
                      : undefined
                  }
                >
                  {item.answer}
                </div>
                <div
                  className={styles.userAvatar}
                  style={
                    accentPalette
                      ? {
                          backgroundColor: accentPalette.sidebarBg,
                          color: accentPalette.headerBg,
                        }
                      : undefined
                  }
                  aria-hidden="true"
                >
                  <Person20Regular className={styles.icon20} />
                </div>
              </div>
            ) : null}
          </div>
        );
      })}

      {currentQuestion && (
        <div className={styles.messageBlock} aria-live="polite">
          <div className={styles.aiBubbleRow}>
            <div
              className={styles.aiAvatar}
              style={
                accentPalette
                  ? {
                      backgroundColor: accentPalette.headerBg,
                      color: accentPalette.headerText,
                    }
                  : undefined
              }
              aria-hidden="true"
            >
              <Bot20Regular className={styles.icon20} />
            </div>
            <div className={styles.aiBubble} aria-label="Current question">
              {currentQuestion}
            </div>
          </div>
        </div>
      )}

      {pendingAnswer && (
        <div className={styles.messageBlock}>
          <div className={styles.userBubbleRow}>
            <div
              className={styles.userBubble}
              style={
                accentPalette
                  ? {
                      backgroundColor: accentPalette.sidebarBg,
                      border: `1px solid ${accentPalette.sidebarBorder}`,
                      color: "#000000",
                    }
                  : undefined
              }
            >
              {pendingAnswer}
            </div>
            <div
              className={styles.userAvatar}
              style={
                accentPalette
                  ? {
                      backgroundColor: accentPalette.sidebarBg,
                      color: accentPalette.headerBg,
                    }
                  : undefined
              }
              aria-hidden="true"
            >
              <Person20Regular className={styles.icon20} />
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className={styles.messageBlock}>
          <div className={styles.aiBubbleRow}>
            <div
              className={styles.aiAvatar}
              style={
                accentPalette
                  ? {
                      backgroundColor: accentPalette.headerBg,
                      color: accentPalette.headerText,
                    }
                  : undefined
              }
              aria-hidden="true"
            >
              <Bot20Regular className={styles.icon20} />
            </div>
            <div
              className={styles.typingBubble}
              role="status"
              aria-label="AI is responding"
            >
              <span className={styles.typingDot} />
              <span
                className={mergeClasses(styles.typingDot, styles.typingDot2)}
              />
              <span
                className={mergeClasses(styles.typingDot, styles.typingDot3)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
